import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { User, CustomerInfo } from "../../services/authService";

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  customer: CustomerInfo | null;
  pharmacyName: string | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  isAuthenticated: !!localStorage.getItem("token"),
  user: localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user")!)
    : null,
  customer: localStorage.getItem("customer")
    ? JSON.parse(localStorage.getItem("customer")!)
    : null,
  pharmacyName: localStorage.getItem("pharmacyName"),
  token: localStorage.getItem("token"),
  loading: false,
  error: null,
};

const persistTenant = (
  customer?: CustomerInfo | null,
  pharmacyName?: string | null
) => {
  if (customer) {
    localStorage.setItem("customer", JSON.stringify(customer));
  }
  if (pharmacyName) {
    localStorage.setItem("pharmacyName", pharmacyName);
    document.title = pharmacyName;
  }
};

const clearTenant = () => {
  localStorage.removeItem("customer");
  localStorage.removeItem("pharmacyName");
  document.title = "Pharmacy Stock Management";
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginStart: (
      state,
      _action: PayloadAction<{ email: string; password: string }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (
      state,
      action: PayloadAction<{
        user: User;
        token: string;
        customer?: CustomerInfo;
        pharmacyName?: string;
      }>
    ) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.customer = action.payload.customer || null;
      state.pharmacyName = action.payload.pharmacyName || null;
      state.error = null;

      localStorage.setItem("token", action.payload.token);
      localStorage.setItem("user", JSON.stringify(action.payload.user));
      persistTenant(action.payload.customer, action.payload.pharmacyName);
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.customer = null;
      state.pharmacyName = null;
      state.token = null;
      state.error = action.payload;

      localStorage.removeItem("token");
      localStorage.removeItem("user");
      clearTenant();
    },

    registerStart: (
      state,
      _action: PayloadAction<{
        name: string;
        email: string;
        password: string;
        role?: string;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    registerSuccess: (
      state,
      action: PayloadAction<{
        user: User;
        token: string;
        customer?: CustomerInfo;
        pharmacyName?: string;
      }>
    ) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.customer = action.payload.customer || null;
      state.pharmacyName = action.payload.pharmacyName || null;
      state.error = null;

      localStorage.setItem("token", action.payload.token);
      localStorage.setItem("user", JSON.stringify(action.payload.user));
      persistTenant(action.payload.customer, action.payload.pharmacyName);
    },
    registerFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    getProfileStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    getProfileSuccess: (
      state,
      action: PayloadAction<{
        user: User;
        customer?: CustomerInfo;
        pharmacyName?: string;
      }>
    ) => {
      state.loading = false;
      state.user = action.payload.user;
      if (action.payload.customer) {
        state.customer = action.payload.customer;
      }
      if (action.payload.pharmacyName) {
        state.pharmacyName = action.payload.pharmacyName;
      }
      state.error = null;

      localStorage.setItem("user", JSON.stringify(action.payload.user));
      persistTenant(
        action.payload.customer || state.customer,
        action.payload.pharmacyName || state.pharmacyName
      );
    },
    getProfileFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    updateProfileStart: (
      state,
      _action: PayloadAction<{ name: string; email: string }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    updateProfileSuccess: (state, action: PayloadAction<User>) => {
      state.loading = false;
      state.user = action.payload;
      state.error = null;

      localStorage.setItem("user", JSON.stringify(action.payload));
    },
    updateProfileFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.customer = null;
      state.pharmacyName = null;
      state.token = null;
      state.loading = false;
      state.error = null;

      localStorage.removeItem("token");
      localStorage.removeItem("user");
      clearTenant();
    },

    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  registerStart,
  registerSuccess,
  registerFailure,
  getProfileStart,
  getProfileSuccess,
  getProfileFailure,
  updateProfileStart,
  updateProfileSuccess,
  updateProfileFailure,
  logout,
  clearError,
} = authSlice.actions;

export default authSlice.reducer;
